/*
  Place des Arts — Recherche NATIVE Shopify (S&D) dans la barre de nav.

  Décision Sam 2026-06-18 : retour au natif (analytics « Top searches » + pertinence
  + données fraîches). Orama ABANDONNÉ pour la recherche (le moteur de FILTRES reste).

  Au focus de la barre (desktop, si déjà une requête) ou au tap de l'icône (mobile),
  ouvre un panneau (téléporté dans <body>) sous le header. À la frappe (debounce), on
  fetch l'endpoint predictif natif et on injecte le rendu serveur :

    GET /search/suggest?q=<q>&section_id=pda-search-suggest
        &resources[type]=product,collection,query&resources[limit]=10&resources[limit_scope]=each
    → sections/pda-search-suggest.liquid (3 zones : Artiste / Collections / Œuvres + rail Suggestions)

  Le panneau réutilise le reveal « 1 morceau » du mega-menu (underlay du header) à partir
  de 1180 seulement ; sous 1180 — y compris à 1024–1179, où la nav reste desktop — il s'ouvre
  en modal PLEIN ÉCRAN, comme en mobile. AUCUNE dépendance Orama / index CDN.
  Submit (Entrée) → /search?q=<q> (page de résultats native → analytics + filtres).
*/

(() => {
  /* Largeurs où la recherche n'a PLUS de barre inline : loupe dans la nav + panneau plein
     écran. Seuil 1179 (décision Sam 24/08, était 1023). À 1024–1179 la nav reste DESKTOP
     (méga-menus, nav inline, favoris, géo) : seule la recherche bascule — la barre inline
     est le seul élément de la rangée à disposer de mou, son champ tombait à 4,00px à 1024.
     ⚠️ SOLIDAIRE du `@media screen and (max-width: 1179px)` de assets/pda-components.css
     (§ recherche hors barre inline). Si l'un des deux bouge sans l'autre, le panneau s'ouvre
     en reveal desktop pendant que la barre est masquée → `activeInput()` rend un <input> en
     display:none et le focus part dans le vide. Les deux seuils se modifient ENSEMBLE. */
  const MQ_LOUPE = window.matchMedia('(max-width: 1179px)');

  /* La recherche suit le MODE de la nav, pas seulement la largeur : `setHeaderMenuStyle()`
     bascule en drawer dès que `isTouchDevice()`, donc AUSSI à ≥1180 (iPad Pro portrait = 1024
     pile, tout iPad en paysage). Sans ça, la barre est masquée mais le panneau s'ouvrirait en
     reveal desktop positionné sous une navbar qui n'existe plus. Correctif 2026-08-08.
     ⚠️ NOM HISTORIQUE : depuis le 24/08 ce prédicat ne veut plus dire « la nav est mobile »
     (à 1024–1179 la nav est desktop) mais « la RECHERCHE est en loupe + plein écran ».
     Renommage non fait ici : `estNavMobile` sert de marqueur d'intégrité du JS servi. */
  const estNavMobile = () =>
    MQ_LOUPE.matches || document.documentElement.classList.contains('pda-nav-drawer');
  const MIN_QUERY = 2;
  const DEBOUNCE = 180;

  // ── état courant (réassigné à chaque init) ──
  let root, cfg, suggestUrl, searchUrl, sectionId, panel, inputs, barInput, panelInput, trigger, closeBtn;
  let resultsEl, liveEl;
  let isOpen = false;
  let openedAsModal = false;
  let lastFocus = null;
  let scrollRaf = 0;
  let debTimer = 0;
  let queryToken = 0;
  let abort = null;
  let painted = false; // squelettes déjà peints pour cette ouverture
  let idlePromise = null; // cache de l'état idle (œuvres populaires)

  // ───────────────────────── helpers ─────────────────────────
  function searchHref(q) {
    return searchUrl + '?q=' + encodeURIComponent(q);
  }
  function escHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
  }
  // Grise la portion TAPÉE du libellé (Figma 1890:55235) : on cherche q (insensible à la
  // casse) dans chaque [data-pda-hl] et on enrobe la correspondance de .pda-psearch__hl.
  function highlightMatch(scope, q) {
    const term = (q || '').trim();
    if (!term || !scope) return;
    const tl = term.toLowerCase();
    scope.querySelectorAll('[data-pda-hl]').forEach((el) => {
      const text = el.textContent;
      const idx = text.toLowerCase().indexOf(tl);
      if (idx === -1) return; // pas de correspondance littérale → libellé en couleur normale
      const before = text.slice(0, idx);
      const match = text.slice(idx, idx + term.length);
      const after = text.slice(idx + term.length);
      el.innerHTML = escHtml(before) + '<span class="pda-psearch__hl">' + escHtml(match) + '</span>' + escHtml(after);
    });
  }

  // Squelettes : réservent la hauteur (Artiste + Œuvres) pendant le fetch → pas de saut.
  function loadingHTML() {
    const row =
      '<div class="pda-psearch-row pda-psearch-row--skeleton" aria-hidden="true">' +
      '<span class="pda-psearch-row__thumb"></span><span class="pda-psearch-row__content"></span></div>';
    const card = '<span class="pda-nsr-card pda-nsr-card--skeleton pda-nsr-card--skeleton-work" aria-hidden="true"></span>';
    return (
      '<div class="pda-psearch" data-pda-cols>' +
      '<div class="pda-psearch__top">' +
      '<div class="pda-psearch__col pda-psearch__col--artists"><p class="pda-psearch__kicker">Artistes</p>' +
      `<div class="pda-psearch__list">${row + row + row}</div></div>` +
      '</div>' +
      '<div class="pda-psearch__oeuvres"><p class="pda-psearch__kicker">Œuvres</p>' +
      `<div class="pda-psearch__row">${card + card + card + card + card}</div></div>` +
      '</div>'
    );
  }
  function setLive(msg) {
    if (liveEl) liveEl.textContent = msg || '';
  }

  // État idle (avant frappe) = œuvres populaires. Rendu par la section HORS /search/suggest
  // (predictive non performé) via /?section_id=pda-search-suggest. Mis en cache (ne change pas).
  function loadIdle() {
    if (!idlePromise) {
      const url = new URL(cfg.rootUrl || '/', location.origin);
      url.searchParams.set('section_id', sectionId);
      idlePromise = fetch(url)
        .then((r) => (r.ok ? r.text() : ''))
        .catch(() => '');
    }
    return idlePromise;
  }
  async function showIdle(token) {
    if (!painted && resultsEl) {
      resultsEl.innerHTML = loadingHTML();
      painted = true;
      syncReveal();
    }
    const html = await loadIdle();
    if (token !== queryToken) return; // une frappe a pris le relais
    injectResults(html, '');
  }

  // ───────────────────────── recherche (natif S&D) ─────────────────────────
  function runSearch(q) {
    const token = ++queryToken;
    if (abort) abort.abort();

    if (q.length < MIN_QUERY) {
      showIdle(token); // avant frappe (ou champ vidé) → œuvres populaires
      return;
    }

    if (!painted && resultsEl) {
      resultsEl.innerHTML = loadingHTML();
      painted = true;
      syncReveal();
    }

    const url = new URL(suggestUrl, location.origin);
    url.searchParams.set('q', q);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('resources[type]', 'product,collection,query');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');

    abort = new AbortController();
    fetch(url, { signal: abort.signal })
      .then((r) => (r.ok ? r.text() : ''))
      .then(async (html) => {
        if (token !== queryToken) return; // frappe plus récente
        /* Option H (25/08, ruling reviewer) : REPÊCHAGE-À-ZÉRO, jamais une
           substitution. Le TAPÉ part d'abord (« man ray » sur /en garde ses
           suggestions telles quelles) ; si le panneau revient VIDE et que la
           réécriture change le terme (« Landschaft » → « Paysage »), on re-fetche
           une fois avec le terme FR et on n'utilise SA réponse que si elle a des
           résultats. Le panneau vide affiché vient donc TOUJOURS du terme tapé
           (son écho `predictive_search.terms` = ce que l'utilisateur a écrit).
           L'affichage (champ, highlight) garde `q` tel que tapé. */
        const qFr = requeteFr(q);
        if (qFr !== q && html.indexOf('pda-search-panel__empty') !== -1) {
          try {
            const urlFr = new URL(url);
            urlFr.searchParams.set('q', qFr);
            const htmlFr = await fetch(urlFr, { signal: abort.signal }).then((r) => (r.ok ? r.text() : ''));
            if (token !== queryToken) return;
            if (htmlFr && htmlFr.indexOf('pda-search-panel__empty') === -1) html = htmlFr;
          } catch (e) {
            /* abort / réseau : on garde le panneau du terme tapé */
          }
        }
        injectResults(html, q);
      })
      .catch(() => {
        /* abort / réseau : on garde l'affichage courant (le formulaire mène à /search) */
      });
  }

  /* Option H (25/08) : réécriture de requête vers la racine FR — helper commun
     (même forme que pda-catalog.js / pda-search-tabs.js). ⛔ PDA absent hors fr =
     panne VISIBLE (console.warn), jamais une identité muette. */
  function requeteFr(t) {
    if (window.PDA && typeof window.PDA.requeteRacineFr === 'function') return window.PDA.requeteRacineFr(t);
    if ((document.documentElement.lang || 'fr').slice(0, 2).toLowerCase() !== 'fr') {
      console.warn('[pda-predictive] PDA.requeteRacineFr indisponible — requête NON réécrite (pda-i18n.js chargé ?)');
    }
    return t;
  }

  function injectResults(html, q) {
    if (!resultsEl) return;
    let cols = null;
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      cols = doc.querySelector('[data-pda-cols]');
    } catch {
      cols = null;
    }
    resultsEl.innerHTML = cols ? cols.outerHTML : '';
    highlightMatch(resultsEl, q); // grise la portion tapée des libellés (suggestions + noms)
    // on GARDE `painted` (les résultats remplacent les squelettes en place →
    // pas de re-flash squelette à chaque frappe ; remis à false par open()/hint).
    syncReveal();
    // annonce a11y : résultats (œuvres + lignes artistes/collections) OU suggestions ; sinon « aucun »
    const n = resultsEl.querySelectorAll(
      '.pda-nsr-card:not(.pda-nsr-card--skeleton), .pda-psearch-row:not(.pda-psearch-row--skeleton)'
    ).length;
    const sugg = resultsEl.querySelectorAll('.pda-psearch__sug').length;
    const empty = resultsEl.querySelector('.pda-search-panel__empty');
    // Annonce LOCALISÉE (clés js:true du bloc pda-i18n — le pluriel passe par les formes
    // one/other de PDA.t, jamais un `n !== 1`). PDA absent ⇒ repli FR, visible au differ.
    const t = window.PDA && typeof window.PDA.t === 'function' ? window.PDA.t : null;
    if (empty || (n === 0 && sugg === 0)) setLive(t ? t('pda.recherche.vide') : 'Aucun résultat.');
    else if (n > 0) setLive(t ? t('pda.catalogue.resultat', { count: n, n: n }) : n + ' résultat' + (n > 1 ? 's' : ''));
    else setLive(t ? t('pda.recherche.suggestion_compte', { count: sugg }) : sugg + ' suggestion' + (sugg > 1 ? 's' : ''));
  }

  // ───────────────────────── positionnement / reveal ─────────────────────────
  let hideTimer = 0; // masquage différé (desktop) après l'animation de sortie
  let panelRO = null; // observe la hauteur du panneau → underlay synchronisé (1 morceau)

  function positionPanel() {
    if (estNavMobile()) return; // plein écran (CSS), pas de calcul
    const header = document.getElementById('header-component') || document.querySelector('.header-section');
    const bottom = header ? header.getBoundingClientRect().bottom : 60;
    if (bottom <= 0) {
      close(); // header sorti du viewport (transparent non-sticky en cours de scroll)
      return;
    }
    panel.style.setProperty('--pda-search-top', bottom + 'px');
  }

  function onScroll() {
    // Sam #4 : ne ferme PLUS au scroll. On garde le panneau collé au header (repositionnement)
    // et la fermeture seulement si le header sort entièrement du viewport (gérée par positionPanel).
    if (isOpen && !estNavMobile()) positionPanel();
  }
  function onResize() {
    if (scrollRaf) return;
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = 0;
      if (isOpen) {
        positionPanel();
        syncReveal();
      }
    });
  }

  // « 1 morceau » comme le mega : on étend l'underlay du header (fond blanc continu navbar +
  // panneau) en posant --submenu-height / --full-open-header-height EXACTEMENT comme
  // header-menu.js (#setFullOpenHeaderHeight). Le panneau est transparent (CSS §4B desktop) →
  // l'underlay le remplit. Resync sur la hauteur réelle (contenu dynamique) via ResizeObserver.
  function syncReveal() {
    if (!isOpen || estNavMobile()) return;
    const header = document.getElementById('header-component');
    if (!header) return;
    const h = panel.offsetHeight;
    if (h <= 0) return;
    header.style.setProperty('--submenu-height', `${h}px`);
    const isOverlap = header.hasAttribute('data-submenu-overlap-bottom-row');
    const headerVisible = isOverlap
      ? header.querySelector('.header__row--top')?.offsetHeight ?? 0
      : header.offsetHeight;
    header.style.setProperty('--full-open-header-height', `${h + headerVisible}px`);
  }
  function clearReveal() {
    const header = document.getElementById('header-component');
    if (!header) return;
    // ne PAS réinitialiser si un mega-menu OU le menu débordement « Plus » est ouvert
    // (header-menu.js possède alors ces vars) : lien mega = aria-expanded sur .menu-list__link ;
    // débordement = aria-expanded / data-overflow-expanded sur l'hôte <menu-list>.
    if (
      header.querySelector('.menu-list__link[aria-expanded="true"]') ||
      header.querySelector('menu-list[aria-expanded="true"], menu-list[data-overflow-expanded="true"]')
    ) {
      return;
    }
    header.style.setProperty('--submenu-height', '0px');
    header.style.setProperty('--full-open-header-height', '0px');
  }

  function activeInput() {
    if (estNavMobile()) return panelInput || barInput;
    return barInput || panelInput;
  }

  // ───────────────────────── ouverture / fermeture ─────────────────────────
  function open() {
    if (isOpen) return;
    isOpen = true;
    painted = false;
    clearTimeout(hideTimer);
    openedAsModal = estNavMobile();
    lastFocus = document.activeElement;
    // état « recherche ouverte » porté par une CLASSE (pas :focus-within, qui tombe au blur
    // avant la fin de l'animation → flash blanc-sur-blanc sur header transparent). Maintenue
    // tant que le panneau est visible (ouverture + fermeture), retirée après l'anim de sortie.
    const headerOpenEl = document.getElementById('header-component');
    if (headerOpenEl) headerOpenEl.classList.add('pda-nav-search-open');
    positionPanel();
    if (!isOpen) return; // positionPanel a pu refermer (header hors écran)
    panel.hidden = false;
    // Reveal façon mega-menu (desktop) : force un frame FERMÉ (reflow) avant --open
    // pour que le clip-path s'anime (sinon peinture directe à l'état ouvert).
    void panel.offsetHeight; // eslint-disable-line no-unused-expressions
    panel.classList.add('pda-search-panel--open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (openedAsModal) {
      panel.setAttribute('aria-modal', 'true');
      document.documentElement.classList.add('pda-search-lock');
      if (panelInput) {
        panelInput.value = barInput ? barInput.value : '';
        panelInput.focus();
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    if (!openedAsModal) {
      if (!panelRO && 'ResizeObserver' in window) panelRO = new ResizeObserver(syncReveal);
      if (panelRO) panelRO.observe(panel);
      requestAnimationFrame(() => requestAnimationFrame(syncReveal));
    }
    // premier rendu = requête courante (invite si < MIN_QUERY)
    const ai = activeInput();
    runSearch((ai && ai.value ? ai.value : '').trim());
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    const wasModal = openedAsModal;
    if (abort) abort.abort();
    panel.classList.remove('pda-search-panel--open');
    panel.removeAttribute('aria-modal');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('pda-search-lock');
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onResize);
    if (panelRO) panelRO.disconnect();
    clearReveal();
    clearTimeout(hideTimer);
    const headerCloseEl = document.getElementById('header-component');
    if (wasModal) {
      panel.hidden = true;
      if (headerCloseEl) headerCloseEl.classList.remove('pda-nav-search-open');
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    } else {
      if (barInput) barInput.blur();
      // on garde la navbar « ouverte » (sombre) jusqu'à la fin de l'anim + collapse de
      // l'underlay, puis on masque le panneau ET on retire la classe → bascule propre,
      // pas de blanc-sur-blanc pendant la fermeture.
      hideTimer = setTimeout(() => {
        if (!isOpen) {
          panel.hidden = true;
          if (headerCloseEl) headerCloseEl.classList.remove('pda-nav-search-open');
        }
      }, 380);
    }
    openedAsModal = false;
  }

  // piège de focus (mobile plein écran = dialog modal)
  function trapFocus(e) {
    if (!openedAsModal || e.key !== 'Tab') return;
    const list = Array.from(panel.querySelectorAll('input, button, a[href]')).filter(
      (el) => !el.hidden && el.offsetParent !== null
    );
    if (!list.length) return;
    const first = list[0];
    const last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // ───────────────────────── événements ─────────────────────────
  function onInput(e) {
    const val = e.target.value;
    inputs.forEach((i) => {
      if (i !== e.target) i.value = val;
    });
    const q = val.trim();
    if (q.length >= MIN_QUERY && !isOpen) open();
    clearTimeout(debTimer);
    debTimer = setTimeout(() => runSearch(q), DEBOUNCE);
  }

  function onFocusBar() {
    // desktop : le focus ouvre le panneau (affiche les œuvres populaires si le champ est vide)
    if (estNavMobile()) return;
    open();
  }

  function onSubmit(input) {
    const q = (input.value || '').trim();
    if (q.length) window.location.assign(searchHref(q));
  }

  // ───────────────────────── init (ré-exécutable : éditeur de thème) ─────────────────────────
  function init(rootEl) {
    if (!rootEl || rootEl.dataset.pdaInit) return;
    const cfgEl = rootEl.querySelector('[data-pda-search-config]');
    if (!cfgEl) return;
    try {
      cfg = JSON.parse(cfgEl.textContent);
    } catch {
      return;
    }
    const tpl = rootEl.querySelector('[data-pda-search-panel-template]');
    if (!tpl) return;

    // Ré-init éditeur de thème : si un panneau précédent était ouvert, on démonte
    // proprement l'ancien (listeners window, ResizeObserver, timers, fetch, underlay,
    // scroll-lock) AVANT de recréer — sinon listeners zombies sur un nœud détaché.
    if (isOpen) {
      isOpen = false;
      openedAsModal = false;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (panelRO) panelRO.disconnect();
      clearTimeout(hideTimer);
      clearTimeout(debTimer);
      if (abort) abort.abort();
      clearReveal();
      document.documentElement.classList.remove('pda-search-lock');
    }

    rootEl.dataset.pdaInit = '1';
    root = rootEl;
    suggestUrl = cfg.suggestUrl || '/search/suggest';
    searchUrl = cfg.searchUrl || '/search';
    sectionId = cfg.sectionId || 'pda-search-suggest';

    // retire un panneau orphelin (ré-init éditeur) avant d'en recréer un
    const stale = document.getElementById('pda-search-panel');
    if (stale) stale.remove();

    panel = tpl.content.firstElementChild.cloneNode(true);
    document.body.appendChild(panel);

    // Backdrop sombre flouté PARTAGÉ (mega + recherche) — créé une fois, montré via CSS
    // (body:has(#header-component.pda-nav-search-open) / :has(mega ouvert)). Figma 1499:142151.
    if (!document.querySelector('.pda-chrome-backdrop')) {
      const bd = document.createElement('div');
      bd.className = 'pda-chrome-backdrop';
      bd.setAttribute('aria-hidden', 'true');
      // dans <body> : position:fixed relative au viewport. z-index SOUS le header → le header
      // (navbar + underlay blanc du panneau, z 8/16) peint par-dessus → seule la page sous le
      // panneau est assombrie. (À l'intérieur de #header-component, fixed se réduirait à 0 car
      // le header est un bloc conteneur position:absolute.)
      document.body.appendChild(bd);
    }

    barInput = root.querySelector('[data-pda-search-input]');
    panelInput = panel.querySelector('[data-pda-search-input]');
    trigger = root.querySelector('[data-pda-search-trigger]');
    closeBtn = panel.querySelector('[data-pda-search-close]');
    const barCloseBtn = root.querySelector('[data-pda-search-bar-close]'); // croix de la barre (desktop)
    inputs = [barInput, panelInput].filter(Boolean);

    resultsEl = panel.querySelector('[data-pda-results]');
    liveEl = panel.querySelector('[data-pda-live]');

    inputs.forEach((input) => {
      input.addEventListener('input', onInput);
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onSubmit(input);
        } else if (e.key === 'Escape') {
          close();
        }
      });
    });
    if (barInput) barInput.addEventListener('focus', onFocusBar);
    if (trigger) trigger.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (barCloseBtn) barCloseBtn.addEventListener('click', close);
    panel.addEventListener('keydown', trapFocus);
    panel.addEventListener('click', (e) => {
      if (e.target.closest('a')) close();
    });

    // La fermeture sur survol/focus d'un item de nav est câblée UNE fois au niveau du document
    // (écoute de `megaMenu:hover`, ci-dessous) — rien à (re)brancher par init.
  }

  // clic-extérieur (le panneau est téléporté dans body → vérifier panel ET root)
  document.addEventListener('pointerdown', (e) => {
    if (!isOpen) return;
    if ((panel && panel.contains(e.target)) || (root && root.contains(e.target))) return;
    close();
  });

  /* #5 (03/08) + bug Sam 19/08 : TOUT survol / focus d'un item de la nav desktop ferme la
     recherche — avec OU SANS panneau. `header-menu.js` `activate()` (on:pointerenter / on:focus
     de chaque .menu-list__list-item) émet `megaMenu:hover` (events.js, bubbles) EN PREMIER, puis
     écrit --submenu-height / --full-open-header-height sur #header-component : la hauteur du
     mega pour un item à panneau, **0 pour un item sans panneau** (« Oeuvres d'exception »).
     Or ces deux variables sont EXACTEMENT celles que syncReveal() pose pour étendre l'underlay
     blanc sous notre panneau transparent. L'ancien déclencheur (MutationObserver sur
     `aria-expanded="true"` d'un .menu-list__link) ne voyait que les items à panneau — un item
     sans panneau ne reçoit jamais cet attribut (fix a11y 19/07) — donc le fond blanc tombait à 0
     et le contenu restait affiché, flottant sur le backdrop. L'événement couvre les deux cas à la
     SOURCE, et arrive AVANT les écritures de activate() (même tâche, synchrone) : close() →
     clearReveal() pose 0, puis activate() pose sa propre hauteur — aucune frame intermédiaire.
     En mode drawer (largeur <1024, tactile, OU overflow `minimum-reached` — les 3 déclencheurs de
     `setHeaderMenuStyle()`, utilities.js) .header-menu est display:none → l'événement ne part pas.
     ⚠️ Trou résiduel PRÉ-EXISTANT, non couvert ici (relecture 19/08) : `#deactivate()` de
     header-menu.js écrit aussi 0/0 au `pointerleave` — souris POSÉE sur un item puis focus
     clavier dans la barre (la garde :hover de deactivate laisse activeItem posé) → en quittant
     l'item, l'underlay tombe sous la recherche ouverte. Cas souris+clavier mêlés, à traiter à la
     SOURCE (événement symétrique côté deactivate), pas en réécrivant les vars en réaction. */
  document.addEventListener('megaMenu:hover', () => {
    if (isOpen) close();
  });

  // bascule desktop↔mobile à chaud : maintenir le scroll-lock cohérent
  function resyncModeNav() {
    if (!isOpen) return;
    openedAsModal = estNavMobile();
    if (openedAsModal) {
      panel.setAttribute('aria-modal', 'true');
      document.documentElement.classList.add('pda-search-lock');
    } else {
      panel.removeAttribute('aria-modal');
      document.documentElement.classList.remove('pda-search-lock');
      positionPanel();
    }
  }
  MQ_LOUPE.addEventListener('change', resyncModeNav);
  /* La bascule peut aussi venir du MODE (setHeaderMenuStyle pose/retire .pda-nav-drawer sur
     <html>) sans que la media query change — cas tablette tactile ≥1024. La SOURCE nous le dit :
     `setHeaderMenuStyle()` émet `pda:nav-mode-change` quand le mode change réellement.
     ⛔ NE PAS écouter `resize` à la place : `setHeaderMenuStyle()` a CINQ appelants
     (header-menu.js connectedCallback ET resize, header.js #updateMenuVisibility sur
     `minimumReached`, theme-editor.js ×2) — un `minimum-reached` bascule le mode SANS resize,
     et le panneau se retrouvait alors en plein écran opaque SANS aria-modal ni scroll-lock
     (mesuré sur le live le 08/08 ; c'est une surface créée par le correctif lui-même).
     ⛔ NI un MutationObserver sur <html> : mesuré le même jour, il GÈLE la page —
     `resyncModeNav` réécrit `pda-search-lock`, ce qui le re-déclenche, et le ping-pong avec
     les autres écritures de cette classe (open/close/hint) sature la file de microtasks
     (symptôme : le clic sur la loupe ne rend jamais la main). L'événement, lui, ne part que sur
     changement réel et aucun consommateur n'écrit `pda-nav-drawer` → pas de boucle. */
  document.addEventListener('pda:nav-mode-change', resyncModeNav);

  // éditeur de thème : le header est re-rendu → ré-init sur le nouveau DOM
  document.addEventListener('shopify:section:load', (e) => {
    const rootEl = e.target && e.target.querySelector ? e.target.querySelector('[data-pda-search-nav]') : null;
    if (rootEl) init(rootEl);
  });

  init(document.querySelector('[data-pda-search-nav]'));
})();
